import { EntityName } from '../../../auth/Security';
import { ADDITIONAL_ENTITY_FIELDS_FOR_DEEP_LINK_UI_STATE } from '../../constants';
import type { DeepLinkUIStateInitializationEvent, DeepLinkUIStateMainEntityNodeObjectValue } from '../../types';
import {
    buildEntityDeepLinkUIStateObject,
    getLinkedEntitySidePanelIdForMainEntityUIState,
    getTabKeyForMainEntityUIState,
    isValidMainEntityNodeObjectValue
} from '../utils';

type DeepLinkInitializationInput = {
    entity: EntityName;
    entityDeepLinkUIStatePath?: string;
    entitySecurityPath: string | undefined;
};

type ResolveAncestorEntityIdDeepLinkPathsInput = {
    deepLinkUIStatePath: string;
    includeResolvedPath?: boolean;
};

export const resolveAncestorEntityIdDeepLinkPaths = ({
    deepLinkUIStatePath,
    includeResolvedPath = true
}: ResolveAncestorEntityIdDeepLinkPathsInput): string[] => {
    const resolvedEntityPath = deepLinkUIStatePath.endsWith('.id')
        ? deepLinkUIStatePath.slice(0, -3)
        : deepLinkUIStatePath;

    const pathSegments = resolvedEntityPath.split('.').filter(Boolean);
    const ancestorEntityIdPaths = pathSegments.map((_, index) => `${pathSegments.slice(0, index + 1).join('.')}.id`);

    if (!includeResolvedPath) {
        return ancestorEntityIdPaths.filter((path) => path !== deepLinkUIStatePath);
    }

    const paths = new Set<string>([deepLinkUIStatePath, ...ancestorEntityIdPaths]);

    return Array.from(paths);
};

export const extractDeepLinkUIStateInitializationEvent = (
    entityUIState: Record<string, unknown> | undefined
): DeepLinkUIStateInitializationEvent => {
    const isValidEntityUIState = isValidMainEntityNodeObjectValue(entityUIState);
    const resolvedEntityUIState = isValidEntityUIState ? entityUIState : undefined;
    const entityId = resolvedEntityUIState?.id;
    const sidePanelId = getLinkedEntitySidePanelIdForMainEntityUIState(resolvedEntityUIState);
    const tabKey = getTabKeyForMainEntityUIState(resolvedEntityUIState);

    const initializationEvent: DeepLinkUIStateInitializationEvent = {
        entityId,
        entityUIState: resolvedEntityUIState,
        sidePanelId,
        tabKey
    };

    return initializationEvent;
};

export const resolveEntityDeepLinkUIStateFromEntityData = <T extends Record<string, unknown>>(
    entity: EntityName,
    entityDataRecord: T
): DeepLinkUIStateMainEntityNodeObjectValue | undefined => {
    const deepLinkStateNodeValue = buildEntityDeepLinkUIStateObject(entity, entityDataRecord);

    if (!deepLinkStateNodeValue) {
        console.warn(
            `Failed to build deep-link state object for entity "${entity}" from entity data record.`,
            entityDataRecord
        );

        return undefined;
    }

    const configuredAdditionalFields = ADDITIONAL_ENTITY_FIELDS_FOR_DEEP_LINK_UI_STATE[entity] ?? [];

    if (configuredAdditionalFields.length > 0) {
        const missingConfiguredAdditionalFields = configuredAdditionalFields.filter(
            (field) => deepLinkStateNodeValue[field] === undefined
        );

        if (missingConfiguredAdditionalFields.length > 0) {
            console.warn(
                `Prepared entity data for "${entity}" is missing configured additional deep-link fields [${missingConfiguredAdditionalFields.join(', ')}]. Proceeding with available values.`
            );
        }
    }

    return deepLinkStateNodeValue;
};
